import React, { useEffect, useId, useRef } from "react"
import { Button, Heading, Text } from "@smooth/design-system-components"

/**
 * 督促画面で使うモーダルダイアログ。CSV 出力のように「押したときだけ情報を出す」導線で使う。
 * 外枠は Tailwind、中身は design system の部品で組む（design_system/POLICY.md）。
 */
export type DunningDialogProps = {
  /** ヘッダの見出し。aria-labelledby でダイアログのアクセシブル名にもなる */
  title: string
  /** 見出しの下に出す補足。1〜2 行を想定 */
  description?: React.ReactNode
  /** Esc・背景クリック・閉じるボタンのいずれでも呼ばれる */
  onClose: () => void
  /** ダイアログ本体 */
  children: React.ReactNode
  /** 右下の操作列。省略時は「閉じる」だけを出す */
  footer?: React.ReactNode
  /** パネルの最大幅。既定は "md"（下の MAX_WIDTH_CLASS を参照） */
  size?: "md" | "lg"
}

const MAX_WIDTH_CLASS: Record<NonNullable<DunningDialogProps["size"]>, string> = {
  md: "max-w-2xl",
  lg: "max-w-4xl",
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

// jsdom は offsetParent を実装していないので可視判定には使わず、hidden 属性だけを除く
const focusableIn = (root: HTMLElement) =>
  Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => !element.hasAttribute("hidden")
  )

export const DunningDialog: React.FC<DunningDialogProps> = ({
  title,
  description,
  onClose,
  children,
  footer,
  size = "md",
}) => {
  const titleId = useId()
  const panelRef = useRef<HTMLDivElement>(null)
  // 閉じたときに開いたボタンへフォーカスを戻すため、開いた時点の活性要素を覚えておく
  const openerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    openerRef.current = document.activeElement as HTMLElement | null
    const panel = panelRef.current
    // 最初のフォーカス可能要素へ。無ければパネル自身（tabIndex=-1）に当てる
    const first = panel ? focusableIn(panel)[0] : undefined
    ;(first ?? panel)?.focus()
    return () => openerRef.current?.focus?.()
  }, [])

  // 背景のスクロールを止める。閉じたら元の overflow に戻す
  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation()
      onClose()
      return
    }
    if (event.key !== "Tab") return
    const panel = panelRef.current
    if (!panel) return
    const focusable = focusableIn(panel)
    if (focusable.length === 0) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    // aria-modal を名乗る以上、Tab はダイアログ内で循環させる
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  return (
    // 背景クリックで閉じる。パネル内のクリックは stopPropagation ではなく target 判定で除く
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-smooth-ds
        className={`max-h-[90vh] w-full ${MAX_WIDTH_CLASS[size]} overflow-y-auto rounded-2xl bg-white p-6 shadow-xl`}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <span id={titleId}>
              <Heading level={2}>{title}</Heading>
            </span>
            {description && (
              <div className="mt-1">
                <Text size="sm" tone="muted">
                  {description}
                </Text>
              </div>
            )}
          </div>
          <button
            type="button"
            aria-label="ダイアログを閉じる"
            onClick={onClose}
            className="shrink-0 text-[1.6rem] leading-none text-black hover:text-black"
          >
            ×
          </button>
        </div>

        <div className="mt-4">{children}</div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
          {footer ?? (
            <Button variant="neutral" onClick={onClose}>
              閉じる
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
